import dayjs, { Dayjs } from 'dayjs';
import { formatDateFromDayjsWithLocale, formatTime24hFromDayjs } from '@utils/dateWithTz';
import { isNullOrUndefined } from '@utils/utils.tsx';

/**
 * Parses a timestamp from the assistant service into the browser's local zone.
 *
 * Chat timestamps are shown in the reader's own time, not a company timezone, so the tz-aware helpers
 * in dateWithTz (which need an explicit timezoneId) don't apply here. FastAPI can emit naive ISO
 * strings with no offset; those are UTC, so they are parsed as such before converting to local time.
 */
export const dayjsFromAssistantDate = (value: string): Dayjs => {
  if (isNullOrUndefined(value)) return null;
  const hasOffset = /(?:Z|[+-]\d{2}:?\d{2})$/.test(value);
  return (hasOffset ? dayjs(value) : dayjs.utc(value)).local();
};

/** `15:54` — the bubble timestamp. */
export const assistantMessageTime = (value: string): string => formatTime24hFromDayjs(dayjsFromAssistantDate(value));

/** `10. 9. 2026` — the day separator between message groups. */
export const assistantMessageDate = (value: string): string => formatDateFromDayjsWithLocale(dayjsFromAssistantDate(value));

/** True when two messages fall on different local days and need a separator between them. */
export const isDifferentDay = (previous: string, current: string): boolean => {
  const a = dayjsFromAssistantDate(previous);
  const b = dayjsFromAssistantDate(current);
  if (isNullOrUndefined(a) || isNullOrUndefined(b)) return false;
  return !a.isSame(b, 'day');
};

/** `Jan Novák` → `JN`, for the avatar circle. */
export const initialsFromName = (firstName?: string, lastName?: string): string => `${firstName?.charAt(0) ?? ''}${lastName?.charAt(0) ?? ''}`.toUpperCase();
